import { useEffect, useState } from "react";
import { api, ArtifactExpiredError, type SpecBundleView } from "../api";
import { ExpiredPlaceholder } from "./ExpiredPlaceholder";

interface Props {
  jobId: string;
}

type State =
  | { kind: "loading" }
  | { kind: "expired"; error: ArtifactExpiredError }
  | { kind: "error"; message: string }
  | { kind: "ready"; bundle: SpecBundleView };

// Cache the dynamically-loaded `marked` module across re-renders so we
// only pay the bundle cost once per session.
let markedPromise: Promise<MarkedFn> | null = null;
type MarkedFn = (md: string) => string;

function loadMarked(): Promise<MarkedFn> {
  if (markedPromise) return markedPromise;
  markedPromise = import("marked").then((mod) => {
    const inst = mod.marked;
    return (md: string) => inst.parse(md, { async: false }) as string;
  });
  return markedPromise;
}

export function SpecTab({ jobId }: Props) {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [marked, setMarked] = useState<MarkedFn | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    Promise.all([api.artifactSpec(jobId), loadMarked()])
      .then(([bundle, m]) => {
        if (cancelled) return;
        setMarked(() => m);
        setState({ kind: "ready", bundle });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ArtifactExpiredError) {
          setState({ kind: "expired", error: e });
        } else {
          setState({
            kind: "error",
            message: e instanceof Error ? e.message : String(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (state.kind === "loading") return <p className="muted">Loading spec…</p>;
  if (state.kind === "expired") return <ExpiredPlaceholder error={state.error} />;
  if (state.kind === "error") return <div className="error">{state.message}</div>;

  const { bundle } = state;
  const sections: Array<[string, string]> = [];
  if (bundle.proposal) sections.push(["proposal.md", bundle.proposal]);
  if (bundle.design) sections.push(["design.md", bundle.design]);
  if (bundle.tasks) sections.push(["tasks.md", bundle.tasks]);
  for (const [cap, body] of Object.entries(bundle.specs)) {
    sections.push([`specs/${cap}/spec.md`, body]);
  }

  if (sections.length === 0)
    return <p className="muted">No spec documents were produced.</p>;

  return (
    <div>
      {sections.map(([name, body]) => (
        <SpecSection key={name} name={name} body={body} marked={marked} />
      ))}
    </div>
  );
}

function SpecSection({
  name,
  body,
  marked,
}: {
  name: string;
  body: string;
  marked: MarkedFn | null;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore — clipboard may be blocked in some contexts
    }
  }
  return (
    <section className="spec-section">
      <header className="row" style={{ marginBottom: 8 }}>
        <code>{name}</code>
        <button className="secondary" onClick={copy}>
          {copied ? "Copied!" : "Copy"}
        </button>
      </header>
      {marked ? (
        <div
          className="markdown-body"
          dangerouslySetInnerHTML={{ __html: marked(body) }}
        />
      ) : (
        <pre className="markdown-fallback">{body}</pre>
      )}
    </section>
  );
}
